/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
} from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import SaveIcon from "@mui/icons-material/Save";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { useAuth } from "auth/Auth";
import { CountrySelect, StateSelect } from "components/GeographySelect";
import { invalidateCountriesCache, invalidateGroupedCitiesCache } from "api/countriesNow";

const cardSx = {
  backgroundColor: "white",
  borderRadius: "12px",
  p: 3,
  boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
};

function GeographySettingsPanel() {
  const { axiosInstance } = useAuth() || {};
  const [tab, setTab] = useState(0);
  const [search, setSearch] = useState("");
  const [countryFilter, setCountryFilter] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const loadRows = useCallback(async () => {
    if (!axiosInstance) return;
    setLoading(true);
    setError(null);
    try {
      let data = [];
      if (tab === 0) {
        const res = await axiosInstance.get("/geography/settings/countries");
        data = res.data || [];
      } else if (tab === 1) {
        if (!countryFilter) {
          setRows([]);
          setDrafts({});
          return;
        }
        const res = await axiosInstance.get("/geography/settings/states", {
          params: { country: countryFilter },
        });
        data = res.data || [];
      } else {
        if (!countryFilter) {
          setRows([]);
          setDrafts({});
          return;
        }
        const res = await axiosInstance.get("/geography/settings/cities", {
          params: {
            country: countryFilter,
            ...(stateFilter ? { state: stateFilter } : {}),
          },
        });
        data = res.data || [];
      }
      setRows(data);
      const nextDrafts = {};
      data.forEach((row) => {
        nextDrafts[row.id] = {
          display_name: row.display_name || row.name || row.master_name || "",
          is_hidden: Boolean(row.is_hidden),
        };
      });
      setDrafts(nextDrafts);
    } catch (err) {
      setError(
        err?.response?.data?.detail?.message ||
          err?.response?.data?.detail ||
          err?.message ||
          "Could not load geography settings."
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [axiosInstance, tab, countryFilter, stateFilter]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  useEffect(() => {
    setSearch("");
    setMessage(null);
    setError(null);
  }, [tab]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => {
      const hay = `${row.master_name || ""} ${row.name || ""} ${row.display_name || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [rows, search]);

  const saveRow = async (row) => {
    if (!axiosInstance) return;
    const draft = drafts[row.id];
    if (!draft) return;
    setSavingId(row.id);
    setMessage(null);
    setError(null);
    try {
      const path =
        tab === 0
          ? `/geography/settings/countries/${row.id}`
          : tab === 1
            ? `/geography/settings/states/${row.id}`
            : `/geography/settings/cities/${row.id}`;
      const res = await axiosInstance.patch(path, {
        display_name: draft.display_name,
        is_hidden: draft.is_hidden,
      });
      const updated = res.data || {};
      setRows((prev) =>
        prev.map((item) =>
          item.id === row.id
            ? {
                ...item,
                name: updated.name,
                display_name: updated.display_name,
                is_hidden: updated.is_hidden,
              }
            : item
        )
      );
      setDrafts((prev) => ({
        ...prev,
        [row.id]: {
          display_name: updated.display_name || updated.name || "",
          is_hidden: Boolean(updated.is_hidden),
        },
      }));
      const renamed = updated.records_updated || 0;
      setMessage(
        renamed > 0
          ? `Saved. Updated ${renamed} existing record${renamed === 1 ? "" : "s"} that used the old name.`
          : "Saved."
      );
      invalidateCountriesCache();
      invalidateGroupedCitiesCache();
    } catch (err) {
      setError(
        err?.response?.data?.detail?.message ||
          err?.response?.data?.detail ||
          err?.message ||
          "Could not save changes."
      );
    } finally {
      setSavingId(null);
    }
  };

  const kindLabel = tab === 0 ? "Countries" : tab === 1 ? "States" : "Cities";

  return (
    <MDBox sx={cardSx}>
      <MDBox display="flex" alignItems="center" gap={1} mb={1}>
        <PublicOutlinedIcon color="info" />
        <MDTypography variant="h6" fontWeight="medium">
          Geography
        </MDTypography>
      </MDBox>
      <MDTypography variant="button" color="text" display="block" mb={2}>
        Rename countries, states, or cities for this workspace, and hide countries you do not want
        in dropdowns. Changes apply only to your tenant and update existing records that used the
        previous name.
      </MDTypography>

      <Tabs
        value={tab}
        onChange={(_, next) => setTab(next)}
        sx={{ mb: 2, borderBottom: "1px solid", borderColor: "divider" }}
      >
        <Tab label="Countries" />
        <Tab label="States" />
        <Tab label="Cities" />
      </Tabs>

      <MDBox display="flex" gap={2} flexWrap="wrap" mb={2} alignItems="center">
        {tab > 0 && (
          <Box sx={{ minWidth: 220, flex: 1 }}>
            <CountrySelect
              label="Country"
              value={countryFilter}
              onChange={(value) => {
                setCountryFilter(value || "");
                setStateFilter("");
              }}
              includeHidden
            />
          </Box>
        )}
        {tab === 2 && (
          <Box sx={{ minWidth: 220, flex: 1 }}>
            <StateSelect
              label="State (optional)"
              country={countryFilter}
              value={stateFilter}
              onChange={(value) => setStateFilter(value || "")}
              includeHidden
            />
          </Box>
        )}
        <TextField
          size="small"
          placeholder={`Search ${kindLabel.toLowerCase()}…`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 220, flex: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlinedIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </MDBox>

      {message && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {typeof error === "string" ? error : "Something went wrong."}
        </Alert>
      )}

      {tab > 0 && !countryFilter ? (
        <MDTypography variant="button" color="text">
          Select a country to manage {kindLabel.toLowerCase()}.
        </MDTypography>
      ) : loading ? (
        <MDBox py={4} display="flex" justifyContent="center">
          <CircularProgress size={28} />
        </MDBox>
      ) : filteredRows.length === 0 ? (
        <MDTypography variant="button" color="text">
          No {kindLabel.toLowerCase()} found.
        </MDTypography>
      ) : (
        <MDBox
          sx={{
            border: "1px solid #E8EDF5",
            borderRadius: "0.75rem",
            overflow: "auto",
            maxHeight: 520,
          }}
        >
          <MDBox
            sx={{
              display: "grid",
              gridTemplateColumns: tab === 0 ? "1.4fr 1.6fr 110px 72px" : "1.4fr 1.6fr 110px 72px",
              bgcolor: "#F4F6F9",
              borderBottom: "1px solid #E8EDF5",
              px: 2,
              py: 1.25,
              position: "sticky",
              top: 0,
              zIndex: 1,
            }}
          >
            {["MASTER NAME", "DISPLAY NAME", tab === 0 ? "HIDDEN" : "HIDDEN", "SAVE"].map((col) => (
              <MDTypography
                key={col}
                variant="caption"
                fontWeight="bold"
                color="text"
                sx={{ letterSpacing: "0.03em" }}
              >
                {col}
              </MDTypography>
            ))}
          </MDBox>

          {filteredRows.map((row) => {
            const draft = drafts[row.id] || {
              display_name: row.name || "",
              is_hidden: false,
            };
            const dirty =
              (draft.display_name || "") !== (row.display_name || row.name || "") ||
              Boolean(draft.is_hidden) !== Boolean(row.is_hidden);
            return (
              <MDBox
                key={row.id}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1.4fr 1.6fr 110px 72px",
                  alignItems: "center",
                  px: 2,
                  py: 1,
                  borderBottom: "1px solid #EEF2F7",
                  bgcolor: row.is_hidden ? "rgba(0,0,0,0.02)" : "transparent",
                }}
              >
                <MDBox>
                  <MDTypography variant="button" fontWeight="medium" color="dark">
                    {row.master_name || row.name}
                  </MDTypography>
                  {row.master_name && row.name && row.master_name !== row.name ? (
                    <MDTypography variant="caption" color="text" display="block">
                      Showing as {row.name}
                    </MDTypography>
                  ) : null}
                </MDBox>
                <TextField
                  size="small"
                  value={draft.display_name}
                  onChange={(e) =>
                    setDrafts((prev) => ({
                      ...prev,
                      [row.id]: { ...draft, display_name: e.target.value },
                    }))
                  }
                  placeholder={row.master_name}
                />
                <Switch
                  checked={Boolean(draft.is_hidden)}
                  onChange={(e) =>
                    setDrafts((prev) => ({
                      ...prev,
                      [row.id]: { ...draft, is_hidden: e.target.checked },
                    }))
                  }
                  color="info"
                  inputProps={{ "aria-label": `Hide ${row.master_name || row.name}` }}
                />
                <Tooltip title={dirty ? "Save changes" : "No changes"} arrow>
                  <span>
                    <IconButton
                      size="small"
                      color="info"
                      disabled={!dirty || savingId === row.id}
                      onClick={() => saveRow(row)}
                    >
                      {savingId === row.id ? (
                        <CircularProgress size={16} />
                      ) : (
                        <SaveIcon fontSize="small" />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              </MDBox>
            );
          })}
        </MDBox>
      )}

      <MDBox mt={2} display="flex" justifyContent="flex-end">
        <MDButton size="small" variant="outlined" color="info" onClick={loadRows} disabled={loading}>
          Refresh
        </MDButton>
      </MDBox>
    </MDBox>
  );
}

export default GeographySettingsPanel;
